import * as React from 'react';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';

import type { Backend } from 'archive/types';
import { DATA_WAREHOUSE_BACKEND_CONFIG_QUERY_KEYS } from 'data-warehouse/Constants';
import type { DataWarehouseConfig } from 'data-warehouse/configurations/hooks/useDataWarehouseConfig';
import useDataWarehouseConfigMutation from 'data-warehouse/configurations/hooks/useDataWarehouseConfigMutation';
import { ConfirmDialog } from 'components/common';
import { Alert, Button } from 'components/bootstrap';

type Props = {
  backend: Backend,
  isActiveBackend: boolean,
  config: DataWarehouseConfig,
}

const DataWarehouseBackendActivateButton = ({ backend, isActiveBackend, config }: Props) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { onUpdateDataWarehouseConfig } = useDataWarehouseConfigMutation();
  const queryClient = useQueryClient();

  const onModalClose = () => {
    setShowModal(false);
  };

  const onButtonClick = () => {
    setShowModal(true);
  };

  const onConfirm = () => {
    onUpdateDataWarehouseConfig({ config: { ...config, active_backend: backend.id } }).then(() => {
      queryClient.invalidateQueries(DATA_WAREHOUSE_BACKEND_CONFIG_QUERY_KEYS);
      onModalClose();
    });
  };

  return (
    <>
      {showModal && (
      <ConfirmDialog show={showModal}
                     onConfirm={onConfirm}
                     onCancel={onModalClose}
                     title="Activate data warehouse backends ?">
        <>
          You are about to activate <strong>{backend.title}</strong> data warehouse backend.
          {!isActiveBackend && (
            <Alert bsStyle="danger">
              <p> You are about to switch currently enabled backend.</p>
              <p>Data already saved to this backend will not be moved to the newly enabled backend.</p>
              <p>Only data from the currently active backend is accessible from Graylog</p>
            </Alert>
          )}
          <p>Are you sure you want to continue?</p>
        </>
      </ConfirmDialog>
      )}
      {!isActiveBackend && (
        <Button bsStyle="default"
                bsSize="medium"
                onClick={onButtonClick}>
          Activate
        </Button>
      )}
    </>
  );
};

DataWarehouseBackendActivateButton.propTypes = {
  backend: PropTypes.object.isRequired,
};

export default DataWarehouseBackendActivateButton;
